import React, {useEffect, useRef} from "react";
import LoungeHeading from "../../../../components/LoungeHeading";
import FormControl from "../../../../components/FormControl";
import {useFetchPaymentGateways} from "../../../../hooks/useFetchPaymentGateways";
import useTiming from "../../../../hooks/useTiming";
import useFetchCountries from "../../../../hooks/useFetchCountries";
import useDonate from "../../../../hooks/forms/useDonate";
import useUser from "../../../../hooks/useUser";
import {useFormatValidationErrors} from "../../../../hooks/response";
import {toast} from "react-toastify";
import { useTranslation } from "react-i18next";

const DonationModule = ({page, body}) => {
  const timing = useTiming();
  const user = useUser();

  const {t} = useTranslation(['lounge', 'global', 'error'])
  const {data:countriesData, status: countriesStatus} = useFetchCountries();
  const {data:paymentGatewaysData, status: paymentGatewaysStatus} = useFetchPaymentGateways({
    event: timing.event.slug,
  }, {
    enabled: !!timing.event.slug,
  });
  const {formik, mutation} = useDonate({
    onError: () => {
      toast(t('error:unable-to-process-donation'), {type: 'error', autoClose: false});
    }
  });
  const errors = useFormatValidationErrors(mutation.error || {}, formik.errors);

  const anchorRef = useRef();
  useEffect(() => {
    mutation.data?.payment_url && anchorRef.current.click();
  }, [mutation.data?.payment_url]);

  return (
    <div>
      <LoungeHeading title={page.data?.title}>
        <div className="lounge-body">
          {body}
        </div>
      </LoungeHeading>

      {mutation.isSuccess ? (
        <div className="bg-white bg-opacity-10 p-5 rounded-lg mb-4">
          <h3 className="text-white text-xl leading-tight tracking-tight font-bold text-center mb-3">
            {t('thank-donation',{name:mutation.data?.details?.name || '', value: mutation.data?.currency.symbol + mutation.data?.amount })}
          </h3>
          <a href={mutation.data?.payment_url} ref={anchorRef} target="_blank" className="btn btn-primary mb-3">{t('complete-donation')}</a>
          <button type="submit" className="btn btn-dark" onClick={() => mutation.reset()}>{t('make-another-donation')}</button>
        </div>
      ) : (
        <div>
          <FormControl.Default className="mb-4 error-light" inputClass="input-light" htmlFor="name" label={t('global:name')} error={formik.touched.name && errors.name}>
            <input type="text" name="name" {...formik.getFieldProps('name')} disabled={!!user} />
          </FormControl.Default>

          <FormControl.Default className="mb-4 error-light" inputClass="input-light" htmlFor="email" label={t('global:email')} error={formik.touched.email && errors.email}>
            <input type="email" name="email" {...formik.getFieldProps('email')} disabled={!!user} />
          </FormControl.Default>

          <FormControl.InlineGroup className="mb-4 error-light" inputClass="input-light" error={(formik.touched.phone && errors.phone) || (formik.touched.phone_country && errors.phone_country)}>
            <FormControl.Select
              label={t('global:country')}
              className="w-2/6 md:3/6 lg:w-2/6 mr-2"
              inputClass="input-light"
              options={countriesStatus !== 'success' ? [
                {value: '', label: countriesStatus === 'error' ? 'Error' : 'Loading'}
              ] : countriesData.map(item => ({value: item.iso_3166_2, label: `${item.name} (+${item.calling_code})`}))}
              {...formik.getFieldProps('phone_country')}
              disabled={!!user}
            />
            <FormControl.Default className="flex-1" inputClass="input-light" htmlFor="phone" label={t('global:phone-number')}>
              <input type="tel" name="phone"  {...formik.getFieldProps('phone')} disabled={!!user}/>
            </FormControl.Default>
          </FormControl.InlineGroup>

          <FormControl.Select
            label={t('global:payment-method')}
            className="mb-4 error-light"
            inputClass="input-light"
            options={paymentGatewaysStatus !== 'success' ? [
              {value: '', label: paymentGatewaysStatus === 'error' ? 'Error' : 'Loading'}
            ] : paymentGatewaysData.map(item => ({value: item.id, label: item.label}))}
            {...formik.getFieldProps('payment_gateway')}
            error={(formik.touched.payment_gateway && errors.payment_gateway)}
          />

          {formik.values.custom_amount ? (
            <FormControl.Default className="mb-4 error-light" inputClass="input-light" htmlFor="amount" label={`Amount (${timing.event.currency.symbol})`} error={(formik.touched.amount && errors.amount)}>
              <input type="number" name="amount" {...formik.getFieldProps('amount')} />
            </FormControl.Default>
          ) : (
            <FormControl.Select
              label={t('global:amount')}
              className="mb-4 error-light"
              inputClass="input-light"
              options={[10,30,50,100,200,300,400,500,1000,3000,5000].map(item => ({value: item, label: `${timing.event.currency.symbol} ${item.toLocaleString('en')}`}))}
              {...formik.getFieldProps('amount')}
              error={(formik.touched.amount && errors.amount)}
            />
          )}

          <FormControl.Checkbox
            light
            className="mb-4 error-light"
            label={t('custom-amount')}
            checked={formik.values.custom_amount}
            onChange={e => formik.setFieldValue('custom_amount', e.target.checked)}
          />

          <FormControl.Default className="mb-4 error-light" inputClass="input-light textarea" htmlFor="remark" label={t('remark')} error={(formik.touched.remark && errors.remark)}>
            <textarea className="h-36" {...formik.getFieldProps('remark')}/>
          </FormControl.Default>

          <button type="submit" className="btn btn-primary" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>{t('donate')}</button>
        </div>
      )}
    </div>
  )
}

export default DonationModule;
